/**
 * ActiveEffectLike (Foundry: rule-element/ae-like.ts): a numeric or structural change to a path
 * on the actor data, in one of several modes, applied in one of four phases.
 */
import { getProperty, setProperty } from "../actor-data";
import { cleanRuleValue, resolveInjectedProperties, resolveValue, type RuleValue } from "../resolve";
import type { EngineState } from "../state";
import type { RuleHandler, RuleInstance } from "./base";

export const AE_LIKE_MODES = ["multiply", "add", "subtract", "remove", "downgrade", "upgrade", "override"] as const;
export type AELikeMode = (typeof AE_LIKE_MODES)[number];
export const AE_LIKE_PHASES = ["applyAEs", "beforeDerived", "afterDerived", "beforeRoll"] as const;
export type AELikePhase = (typeof AE_LIKE_PHASES)[number];

export const CHANGE_MODE_DEFAULT_PRIORITIES: Record<AELikeMode, number> = {
  multiply: 10,
  add: 20,
  subtract: 20,
  remove: 20,
  downgrade: 30,
  upgrade: 40,
  override: 50,
};

interface AELikeData {
  mode: AELikeMode;
  path: string;
  phase: AELikePhase;
  value: RuleValue;
  merge: boolean;
  testDomains: string[];
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function parse(rule: RuleInstance): void {
  const s = rule.source;
  if (!(AE_LIKE_MODES as readonly string[]).includes(String(s.mode))) return rule.fail(`mode: "${String(s.mode)}" is not a change mode`);
  if (typeof s.path !== "string" || !s.path) return rule.fail("path: must be a non-empty string");
  const phase = s.phase === undefined ? "applyAEs" : s.phase;
  if (!(AE_LIKE_PHASES as readonly string[]).includes(String(phase))) return rule.fail(`phase: "${String(phase)}" is not a data preparation phase`);
  if (!("value" in s)) return rule.fail("value: may not be undefined");
  const value = cleanRuleValue(s.value, true);
  if (value === undefined) return rule.fail("value: may not be undefined");
  const merge = s.merge === true;
  if (merge && s.mode !== "override") return rule.fail('mode must be "override" if merge is true');
  if (merge && !isPlainObject(value)) return rule.fail("value must be an object if merge is true");
  const testDomains = Array.isArray(s.testDomains) ? s.testDomains.filter((d): d is string => typeof d === "string" && !!d) : [];
  const data: AELikeData = { mode: s.mode as AELikeMode, path: s.path, phase: phase as AELikePhase, value, merge, testDomains };
  rule.data = data as unknown as Record<string, unknown>;
}

function pathIsValid(state: EngineState, path: string): boolean {
  if (path.startsWith("data.") || /\bnull\b/.test(path)) return false;
  if (path.startsWith("flags.")) return true;
  const candidates = [path, path.replace(/\.[-\w]+$/, ""), path.replace(/\.?[-\w]+\.[-\w]+$/, "")];
  return candidates.some((p) => getProperty(state.actor, p) !== undefined);
}

export class ChangeFailure {
  constructor(readonly message: string) {}
}

/** Compute the new value for a mode (Foundry: AELikeRuleElement.getNewValue). */
export function getNewValue(mode: AELikeMode, current: unknown, change: unknown, merge = false): unknown {
  const addOrSubtract = (value: unknown): unknown => {
    const isNumericAdd = typeof value === "number" && (typeof current === "number" || current === undefined || current === null);
    const isArrayAdd = Array.isArray(current) && current.every((e) => typeof e === typeof value);
    if (isNumericAdd) return ((current as number | null | undefined) ?? 0) + value;
    if (isArrayAdd) return value;
    return new ChangeFailure(`${String(value)} cannot be added to ${String(current)}`);
  };
  switch (mode) {
    case "multiply": {
      if (typeof change !== "number") return new ChangeFailure(`${String(change)} is not a number`);
      if (!(typeof current === "number" || current === undefined)) return new ChangeFailure(`${String(current)} cannot be multiplied`);
      return Math.trunc((current ?? 0) * change);
    }
    case "add":
      return addOrSubtract(change);
    case "subtract":
    case "remove": {
      const added = (typeof current === "number" || current === undefined) && typeof change === "number" ? -1 * change : change;
      return addOrSubtract(added);
    }
    case "downgrade": {
      if (typeof change !== "number") return new ChangeFailure(`${String(change)} is not a number`);
      if (!(typeof current === "number" || current === undefined)) return new ChangeFailure(`${String(current)} cannot be downgraded`);
      return Math.min(current ?? 0, change);
    }
    case "upgrade": {
      if (typeof change !== "number") return new ChangeFailure(`${String(change)} is not a number`);
      if (!(typeof current === "number" || current === undefined)) return new ChangeFailure(`${String(current)} cannot be upgraded`);
      return Math.max(current ?? 0, change);
    }
    case "override": {
      const isOverridable =
        current === null ||
        current === undefined ||
        typeof current === typeof change ||
        (["number", "boolean"].includes(typeof current) && ["number", "boolean"].includes(typeof change));
      if (merge && isPlainObject(current) && isPlainObject(change)) return { ...current, ...change };
      if (!isOverridable) return new ChangeFailure(`${String(change)} cannot override ${String(current)}`);
      return change;
    }
  }
}

function apply(rule: RuleInstance, state: EngineState, rollOptions?: Set<string>): void {
  if (rule.ignored) return;
  const data = rule.data as unknown as AELikeData;
  const path = resolveInjectedProperties(rule, state, data.path);
  if (rule.ignored) return;
  if (!pathIsValid(state, path)) return rule.fail(`no data found at or near "${path}"`);

  rollOptions ??= rule.predicate.length > 0 ? state.rollOptions.get(data.testDomains) : new Set();
  if (!rule.test(state, rollOptions)) return;

  const actor = state.actor as unknown as Record<string, unknown>;
  const current = getProperty(actor, path);
  const change = resolveValue(rule, state, data.value);
  if (rule.ignored) return;
  const newValue = getNewValue(data.mode, current, change, data.merge);
  if (newValue instanceof ChangeFailure) return rule.fail(newValue.message);

  if (data.mode === "add" && Array.isArray(current)) {
    if (!current.includes(newValue)) current.push(newValue);
  } else if ((data.mode === "subtract" || data.mode === "remove") && Array.isArray(current)) {
    const index = current.indexOf(newValue);
    if (index !== -1) current.splice(index, 1);
  } else {
    setProperty(actor, path, newValue);
    logChange(rule, state, data, change);
  }
}

function logChange(rule: RuleInstance, state: EngineState, data: AELikeData, value: unknown): void {
  if (!(typeof value === "boolean" || typeof value === "number" || typeof value === "string" || value === null)) return;
  const item = rule.item;
  const level = item.type === "feat" ? (item.levelTaken ?? item.level) : item.level;
  (state.actor.system.autoChanges[data.path] ??= []).push({ mode: data.mode, level, value, source: item.name });
}

const phaseIs = (rule: RuleInstance, phase: AELikePhase): boolean => (rule.data as unknown as AELikeData).phase === phase;

export const ActiveEffectLikeHandler: RuleHandler = {
  key: "ActiveEffectLike",
  priority: (source) => CHANGE_MODE_DEFAULT_PRIORITIES[String(source.mode) as AELikeMode] ?? 50,
  parse,
  onApplyActiveEffects: (rule, state) => { if (phaseIs(rule, "applyAEs")) apply(rule, state); },
  beforePrepareData: (rule, state) => { if (phaseIs(rule, "beforeDerived")) apply(rule, state); },
  afterPrepareData: (rule, state) => { if (phaseIs(rule, "afterDerived")) apply(rule, state); },
};
